import { ref } from 'vue';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

export function usePasskey() {
    const loading = ref(false);
    const error = ref(null);

    /**
     * Check if passkeys are supported in the current browser
     */
    const isSupported = () => {
        return typeof window.browserSupportsWebAuthn === 'function' && window.browserSupportsWebAuthn();
    };

    /**
     * Register a new passkey for the authenticated user
     */
    const register = async (name = null) => {
        if (!isSupported()) {
            throw new Error('Passkeys are not supported in this browser');
        }

        loading.value = true;
        error.value = null;

        try {
            // Step 1: Get registration options from server
            const optionsResponse = await ApiService.apiCall('/webauthn/register/options', 'post');
            const options = optionsResponse.data;

            // Step 2: Trigger browser's passkey creation
            const credential = await window.startRegistration({ optionsJSON: options });

            // Step 3: Send credential to server for storage
            const registerResponse = await ApiService.apiCall('/webauthn/register', 'post', {
                ...credential,
                name: name,
            });

            return registerResponse.data;
        } catch (err) {
            error.value = err.response?.data?.message || err.message || 'Registration failed';
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Authenticate using a passkey
     */
    const authenticate = async () => {
        if (!isSupported()) {
            throw new Error('Passkeys are not supported in this browser');
        }

        loading.value = true;
        error.value = null;

        try {
            // Step 1: Get authentication options from server
            const optionsResponse = await ApiService.apiCall('/webauthn/login/options', 'post');
            const options = optionsResponse.data;

            // Step 2: Trigger browser's passkey authentication
            const assertion = await window.startAuthentication({ optionsJSON: options });

            // Step 3: Send assertion to server for verification
            const authResponse = await ApiService.apiCall('/webauthn/login', 'post', assertion);

            return authResponse.data;
        } catch (err) {
            error.value = err.response?.data?.message || err.message || 'Authentication failed';
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Delete a passkey
     */
    const deletePasskey = async (passkeyId) => {
        loading.value = true;
        error.value = null;

        try {
            await ApiService.apiCall(`/passkeys/${passkeyId}`, 'delete');
            return true;
        } catch (err) {
            error.value = err.response?.data?.message || err.message || 'Delete failed';
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Rename a passkey
     */
    const renamePasskey = async (passkeyId, newName) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await ApiService.apiCall(`/passkeys/${passkeyId}`, 'put', {
                name: newName,
            });
            return response.data;
        } catch (err) {
            error.value = err.response?.data?.message || err.message || 'Rename failed';
            throw err;
        } finally {
            loading.value = false;
        }
    };

    return {
        loading,
        error,
        isSupported,
        register,
        authenticate,
        deletePasskey,
        renamePasskey,
    };
}
